import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Download, Share, Plus } from "lucide-react";
import { isNativeBuild } from "@/lib/config";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const IOS_DISMISS_KEY = "vyzle-ios-install-dismissed";

// iOS Safari never fires `beforeinstallprompt`, so the only way to install is
// the manual Share → "Add to Home Screen" flow. Detect iOS and surface a hint.
function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ Safari reports as a Mac; disambiguate via touch points.
  const iPadOS = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

// True when the app is already launched from the home screen (installed).
function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    (navigator as any).standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  // iOS-specific state
  const [showIosButton, setShowIosButton] = useState(false);
  const [iosModalOpen, setIosModalOpen] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    // Show the iOS install hint only on iOS Safari, when not already installed
    // and not previously dismissed.
    if (isIOS() && !isStandalone()) {
      try {
        if (localStorage.getItem(IOS_DISMISS_KEY) !== "1") {
          setShowIosButton(true);
        }
      } catch {
        setShowIosButton(true);
      }
    }
  }, []);

  const onInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setVisible(false);
      }
    } catch {
      setVisible(false);
    }
  };

  const onDismiss = () => setVisible(false);

  // Inside the native shell the app is already installed, but the iOS branch
  // below would still fire: the WebView's UA says iPhone and `standalone` is
  // false, so it would offer "Add to Home Screen" to someone who installed
  // from the App Store. Nothing here applies to a native build.
  if (isNativeBuild) return null;

  const dismissIos = () => {
    setShowIosButton(false);
    setIosModalOpen(false);
    try {
      localStorage.setItem(IOS_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  // Android / Chrome native install flow.
  if (visible && deferredPrompt) {
    return (
      <div className="fixed inset-x-0 bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] z-40 px-3 flex justify-center">
        <div className="flex items-center gap-2 rounded-full bg-background/95 border border-border shadow-lg px-3 py-2">
          <Button onClick={onInstall} size="sm" className="rounded-full h-8 px-3 bg-[hsl(128,60%,54%)] hover:bg-[hsl(128,60%,49%)] text-white">
            <Download className="w-4 h-4 mr-1" /> Install app
          </Button>
          <Button variant="ghost" size="sm" onClick={onDismiss} className="h-8 px-3">
            Later
          </Button>
        </div>
      </div>
    );
  }

  // iOS install hint (Add to Home Screen instructions).
  if (showIosButton) {
    return (
      <>
        <div className="fixed inset-x-0 bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] z-40 px-3 flex justify-center">
          <div className="flex items-center gap-2 rounded-full bg-background/95 border border-border shadow-lg px-3 py-2">
            <Button
              onClick={() => setIosModalOpen(true)}
              size="sm"
              className="rounded-full h-8 px-3 bg-[hsl(128,60%,54%)] hover:bg-[hsl(128,60%,49%)] text-white"
            >
              <Download className="w-4 h-4 mr-1" /> Install app
            </Button>
            <Button variant="ghost" size="sm" onClick={dismissIos} className="h-8 px-3">
              Later
            </Button>
          </div>
        </div>

        <Dialog open={iosModalOpen} onOpenChange={setIosModalOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Install Vyzle</DialogTitle>
              <DialogDescription>
                Add Vyzle to your home screen to launch it like a native app — full screen, no browser bar.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(128,60%,54%)]/10 text-[hsl(128,60%,44%)]">
                  <Share className="h-5 w-5" />
                </div>
                <p className="text-sm leading-9 text-foreground">
                  Tap the <span className="font-semibold">Share</span> button in Safari's toolbar.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(128,60%,54%)]/10 text-[hsl(128,60%,44%)]">
                  <Plus className="h-5 w-5" />
                </div>
                <p className="text-sm leading-9 text-foreground">
                  Choose <span className="font-semibold">Add to Home Screen</span>, then tap <span className="font-semibold">Add</span>.
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={dismissIos}
                size="sm"
                className="rounded-full h-8 px-4 bg-[hsl(128,60%,54%)] hover:bg-[hsl(128,60%,49%)] text-white"
              >
                Got it
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return null;
}
